import { useMemo } from "react";
import { SUPPORTED_AIRDROP_FARMING_SPOT_EXCHANGES, FEATURE_FLAGS } from "@cede/types";
import type { AirdropFarmingTabId } from "@/constants/airdrop-farming";
import { AIRDROP_FARMING_TAB_IDS } from "@/constants/airdrop-farming";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";

/**
 * Strategy-agnostic feature-flag view for the hub-v2 airdrop-farming surface.
 * An exchange is "enabled" when EITHER its mm or dn flag is on — same for
 * maintenance. For strategy-specific gating, use Flow's hook
 * (`use-trading-feature-flags`) which accepts a strategy arg.
 */
export function useAirdropFarmingFeatureFlags() {
  const isGridEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_STRATEGY_GRID_ENABLE);
  const isDeltaNeutralEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_STRATEGY_DELTA_NEUTRAL_ENABLE);

  const isHyperliquidMm = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_HYPERLIQUID_MM_ENABLE);
  const isHyperliquidDn = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_HYPERLIQUID_DN_ENABLE);
  const isParadexMm = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_PARADEX_MM_ENABLE);
  const isParadexDn = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_PARADEX_DN_ENABLE);
  const isGrvtMm = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_GRVT_MM_ENABLE);
  const isGrvtDn = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_GRVT_DN_ENABLE);
  const isZerooneMm = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_ZEROONE_MM_ENABLE);
  const isZerooneDn = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_ZEROONE_DN_ENABLE);
  const isExtendedMm = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_EXTENDED_MM_ENABLE);
  const isExtendedDn = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_EXTENDED_DN_ENABLE);
  const isHibachiMm = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_HIBACHI_MM_ENABLE);
  const isHibachiDn = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_HIBACHI_DN_ENABLE);
  const isBitgetMm = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_BITGET_MM_ENABLE);
  const isBitgetDn = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_EXCHANGE_BITGET_DN_ENABLE);

  const isBotCreationDisabled = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_FLOW_BOT_CREATION_DISABLE);

  const isHyperliquidMmMaintenance = useFeatureFlagIsOn(
    FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_HYPERLIQUID_MM_MAINTENANCE,
  );
  const isHyperliquidDnMaintenance = useFeatureFlagIsOn(
    FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_HYPERLIQUID_DN_MAINTENANCE,
  );
  const isParadexMmMaintenance = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_PARADEX_MM_MAINTENANCE);
  const isParadexDnMaintenance = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_PARADEX_DN_MAINTENANCE);
  const isGrvtMmMaintenance = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_GRVT_MM_MAINTENANCE);
  const isGrvtDnMaintenance = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_GRVT_DN_MAINTENANCE);
  const isZerooneMmMaintenance = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_ZEROONE_MM_MAINTENANCE);
  const isZerooneDnMaintenance = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_ZEROONE_DN_MAINTENANCE);
  const isExtendedMmMaintenance = useFeatureFlagIsOn(
    FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_EXTENDED_MM_MAINTENANCE,
  );
  const isExtendedDnMaintenance = useFeatureFlagIsOn(
    FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_EXTENDED_DN_MAINTENANCE,
  );
  const isHibachiMmMaintenance = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_HIBACHI_MM_MAINTENANCE);
  const isHibachiDnMaintenance = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_HIBACHI_DN_MAINTENANCE);
  const isBitgetMmMaintenance = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_BITGET_MM_MAINTENANCE);
  const isBitgetDnMaintenance = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_FLOW_EXCHANGE_BITGET_DN_MAINTENANCE);

  // Master spot switch — when off, hides spot trading from the launch flow with a "Coming soon" hint
  // regardless of per-exchange flags below.
  const isSpotEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_HUB_CEX_SPOT_ENABLE);
  const isMexcSpotEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_CEX_SPOT_MEXC_ENABLE);
  const isKucoinSpotEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_CEX_SPOT_KUCOIN_ENABLE);
  const isCryptocomSpotEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_CEX_SPOT_CRYPTOCOM_ENABLE);
  const isBybitSpotEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.AIRDROP_FARMING_API_CEX_SPOT_BYBIT_ENABLE);

  const exchangeEnabledMap: Record<AirdropFarmingTabId, boolean> = useMemo(
    () => ({
      hyperliquid: isHyperliquidMm || isHyperliquidDn,
      paradex: isParadexMm || isParadexDn,
      grvt: isGrvtMm || isGrvtDn,
      zeroone: isZerooneMm || isZerooneDn,
      extended: isExtendedMm || isExtendedDn,
      hibachi: isHibachiMm || isHibachiDn,
      bitget: isBitgetMm || isBitgetDn,
    }),
    [
      isHyperliquidMm,
      isHyperliquidDn,
      isParadexMm,
      isParadexDn,
      isGrvtMm,
      isGrvtDn,
      isZerooneMm,
      isZerooneDn,
      isExtendedMm,
      isExtendedDn,
      isHibachiMm,
      isHibachiDn,
      isBitgetMm,
      isBitgetDn,
    ],
  );

  const enabledExchangeTabs = useMemo(
    () => AIRDROP_FARMING_TAB_IDS.filter((id) => exchangeEnabledMap[id]),
    [exchangeEnabledMap],
  );

  const maintenanceMap: Record<AirdropFarmingTabId, boolean> = useMemo(
    () => ({
      hyperliquid: isHyperliquidMmMaintenance || isHyperliquidDnMaintenance,
      paradex: isParadexMmMaintenance || isParadexDnMaintenance,
      grvt: isGrvtMmMaintenance || isGrvtDnMaintenance,
      zeroone: isZerooneMmMaintenance || isZerooneDnMaintenance,
      extended: isExtendedMmMaintenance || isExtendedDnMaintenance,
      hibachi: isHibachiMmMaintenance || isHibachiDnMaintenance,
      bitget: isBitgetMmMaintenance || isBitgetDnMaintenance,
    }),
    [
      isHyperliquidMmMaintenance,
      isHyperliquidDnMaintenance,
      isParadexMmMaintenance,
      isParadexDnMaintenance,
      isGrvtMmMaintenance,
      isGrvtDnMaintenance,
      isZerooneMmMaintenance,
      isZerooneDnMaintenance,
      isExtendedMmMaintenance,
      isExtendedDnMaintenance,
      isHibachiMmMaintenance,
      isHibachiDnMaintenance,
      isBitgetMmMaintenance,
      isBitgetDnMaintenance,
    ],
  );

  const cexSpotEnabledMap: Record<string, boolean> = useMemo(
    () => ({
      mexc: isMexcSpotEnabled,
      kucoin: isKucoinSpotEnabled,
      cryptocom: isCryptocomSpotEnabled,
      bybit: isBybitSpotEnabled,
    }),
    [isMexcSpotEnabled, isKucoinSpotEnabled, isCryptocomSpotEnabled, isBybitSpotEnabled],
  );

  const enabledSpotExchanges = useMemo(
    () => (isSpotEnabled ? SUPPORTED_AIRDROP_FARMING_SPOT_EXCHANGES.filter((id) => cexSpotEnabledMap[id]) : []),
    [isSpotEnabled, cexSpotEnabledMap],
  );

  return {
    isGridEnabled,
    isDeltaNeutralEnabled,
    isExchangeEnabled: (tabId: AirdropFarmingTabId) => exchangeEnabledMap[tabId],
    enabledExchangeTabs,
    enabledSpotExchanges,
    isBotCreationDisabled,
    isExchangeInMaintenance: (tabId: AirdropFarmingTabId) => maintenanceMap[tabId],
  };
}
